import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useCeremony } from '../../hooks/useCeremony';
import { isRetryable } from '../../api/apiError';
import CeremonyShell from './components/CeremonyShell';
import CeremonyHero from './components/CeremonyHero';
import InfoCard from './components/InfoCard';
import CeremonyLoading from './components/CeremonyLoading';
import CeremonyError from './components/CeremonyError';
import PhotoGallery from './components/PhotoGallery';
import './CeremonyPage.css';
import './components/ceremony-components.css';
import './components/photo-gallery.css';

const DEFAULT_TITLE = 'دعوة الحفل';

/**
 * Route: /ceremony/:ceremonyId?access_key=...
 *
 * `ceremonyId` comes from the route param; `access_key` is read from the
 * query string (not a route param) via useSearchParams, matching the QR
 * URL contract documented in CEREMONY_QR_PAGE.md. Neither value is ever
 * logged — see src/api/ceremonyApi.js and useCeremony.js.
 */
export default function CeremonyPage() {
  const { ceremonyId } = useParams();
  const [searchParams] = useSearchParams();
  const accessKey = searchParams.get('access_key');

  const { status, data, error, retry } = useCeremony(ceremonyId, accessKey);

  useEffect(() => {
    document.title = status === 'success' && data?.name ? `${data.name} — دعوة الحفل` : DEFAULT_TITLE;
    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [status, data]);

  return (
    <CeremonyShell>
      {status === 'loading' && <CeremonyLoading />}

      {status === 'error' && error && (
        <CeremonyError error={error} onRetry={isRetryable(error.kind) ? retry : undefined} />
      )}

      {status === 'success' && data && (
        <>
          {data.seat_number && (
            <section className="ceremony-page__details" aria-label="تفاصيل الحفل">
              <InfoCard label="رقم المقعد" value={data.seat_number} delay={0} />
            </section>
          )}

          <CeremonyHero name={data.name} />

          <PhotoGallery photos={data.photos} />

          <footer className="ceremony-footer">
            <p>لا نطيق الانتظار للاحتفال معكم.</p>
          </footer>
        </>
      )}
    </CeremonyShell>
  );
}
